/**
 * @typedef {Object} StoreSearchHistoryItem
 * @property {string} displayText - 展示给用户的搜索词
 * @property {string} keyword - 实际请求接口使用的搜索词
 */

/**
 * @description 统一清洗文本输入，避免搜索关键字因空格或非字符串导致分支漂移
 * @param {unknown} value - 原始文本值
 * @returns {string}
 */
const normalizeText = (value) => {
  if (typeof value === 'string') {
    return value.trim()
  }

  if (value == null) {
    return ''
  }

  return String(value).trim()
}

/**
 * @description 生成忽略大小写的比较文本，兼容热门词匹配与历史去重
 * @param {unknown} value - 待比较文本
 * @returns {string}
 */
const toComparableText = (value) => normalizeText(value).toLowerCase()

/**
 * @description 获取热门搜索展示词
 * @param {string|Record<string, any>} item - 热门搜索项
 * @returns {string}
 */
export const getHotSearchDisplayText = (item) => {
  if (typeof item === 'string') {
    return normalizeText(item)
  }

  return normalizeText(
    item?.title || item?.displayText || item?.keyword || item?.name || item?.content
  )
}

/**
 * @description 获取热门搜索真实请求词
 * @param {string|Record<string, any>} item - 热门搜索项
 * @returns {string}
 */
export const getHotSearchKeyword = (item) => {
  if (typeof item === 'string') {
    return normalizeText(item)
  }

  return normalizeText(
    item?.content || item?.keyword || item?.title || item?.displayText || item?.name
  )
}

/**
 * @description 规范化单条搜索历史，统一为 displayText + keyword 双字段结构
 * @param {string|Record<string, any>} item - 原始历史项
 * @returns {StoreSearchHistoryItem|null}
 */
export const normalizeSearchHistoryItem = (item) => {
  if (typeof item === 'string') {
    const text = normalizeText(item)
    return text ? { displayText: text, keyword: text } : null
  }

  if (!item || typeof item !== 'object') {
    return null
  }

  const displayText = normalizeText(
    item.displayText || item.title || item.name || item.keyword || item.content
  )
  const keyword = normalizeText(
    item.keyword || item.content || item.displayText || item.title || item.name
  )

  if (!displayText && !keyword) {
    return null
  }

  return {
    displayText: displayText || keyword,
    keyword: keyword || displayText,
  }
}

/**
 * @description 批量规范化搜索历史列表
 * @param {Array<string|Record<string, any>>} list - 原始历史列表
 * @returns {StoreSearchHistoryItem[]}
 */
export const normalizeSearchHistoryList = (list) => {
  if (!Array.isArray(list)) {
    return []
  }

  return list.map((item) => normalizeSearchHistoryItem(item)).filter((item) => Boolean(item))
}

/**
 * @description 合并多份搜索历史，并优先保留前面列表中的展示词
 * @param {...Array<string|Record<string, any>>} lists - 待合并的历史列表
 * @returns {StoreSearchHistoryItem[]}
 */
export const mergeSearchHistoryLists = (...lists) => {
  const mergedList = []
  const seenKeys = new Set()

  lists.forEach((list) => {
    normalizeSearchHistoryList(list).forEach((item) => {
      const dedupeKey = toComparableText(item.keyword || item.displayText)
      if (!dedupeKey || seenKeys.has(dedupeKey)) {
        return
      }
      seenKeys.add(dedupeKey)
      mergedList.push(item)
    })
  })

  return mergedList
}

/**
 * @description 获取搜索历史展示词
 * @param {string|Record<string, any>} item - 历史项
 * @returns {string}
 */
export const getSearchHistoryDisplayText = (item) => {
  return normalizeSearchHistoryItem(item)?.displayText || ''
}

/**
 * @description 获取搜索历史真实请求词
 * @param {string|Record<string, any>} item - 历史项
 * @returns {string}
 */
export const getSearchHistoryKeyword = (item) => {
  return normalizeSearchHistoryItem(item)?.keyword || ''
}

/**
 * @description 根据展示词在热门搜索中寻找真实请求词
 * @param {string} displayText - 输入框或路由中的展示词
 * @param {Array<string|Record<string, any>>} hotSearchList - 热门搜索列表
 * @returns {string}
 */
export const findHotSearchKeywordByDisplayText = (displayText, hotSearchList = []) => {
  const comparableDisplayText = toComparableText(displayText)
  if (!comparableDisplayText || !Array.isArray(hotSearchList)) {
    return ''
  }

  const matchedItem = hotSearchList.find((item) => {
    return toComparableText(getHotSearchDisplayText(item)) === comparableDisplayText
  })

  return matchedItem ? getHotSearchKeyword(matchedItem) : ''
}

/**
 * @description 解析搜索请求词，优先使用最新热门搜索配置，其次回退到显式传入的真实关键字
 * @param {Object} params - 解析参数
 * @param {string} params.displayText - 展示词
 * @param {string} [params.actualKeyword] - 路由或历史里保存的真实请求词
 * @param {Array<string|Record<string, any>>} [params.hotSearchList] - 热门搜索列表
 * @returns {string}
 */
export const resolveStoreSearchKeyword = ({
  displayText,
  actualKeyword = '',
  hotSearchList = [],
}) => {
  const normalizedDisplayText = normalizeText(displayText)
  if (!normalizedDisplayText) {
    return ''
  }

  const hotSearchKeyword = findHotSearchKeywordByDisplayText(normalizedDisplayText, hotSearchList)
  if (hotSearchKeyword) {
    return hotSearchKeyword
  }

  return normalizeText(actualKeyword) || normalizedDisplayText
}

/**
 * @description 生成商城搜索链路需要的展示词、真实请求词与路由 query
 * @param {Object} params - 构建参数
 * @param {string} params.displayText - 展示词
 * @param {string} [params.actualKeyword] - 显式指定的真实请求词
 * @param {Array<string|Record<string, any>>} [params.hotSearchList] - 热门搜索列表
 * @returns {{ displayText: string, keyword: string, query: Record<string, string> }}
 */
export const createStoreSearchPayload = ({
  displayText,
  actualKeyword = '',
  hotSearchList = [],
}) => {
  const normalizedDisplayText = normalizeText(displayText)
  const keyword = resolveStoreSearchKeyword({
    displayText: normalizedDisplayText,
    actualKeyword,
    hotSearchList,
  })

  return {
    displayText: normalizedDisplayText,
    keyword,
    query: normalizedDisplayText ? { q: normalizedDisplayText } : {},
  }
}
